import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fetchDeductionReport, DeductionRecord } from '../../../../api/salary/deduction.service';
import TimesheetReportsHeader from './TimesheetReportsHeader';

interface ReportFilter {
    startDate: string;
    endDate: string;
    customerFilter: string;
    searchQuery: string;
}

interface ReportData {
    id: string;
    timeSheetWorkingHourId: number;
    date: string;
    invoiceNo: string;
    clientName: string;
    taskDetails: string;
    pricingType: string;
    price: string;
    hours: string;
}

const TimesheetReports: React.FC = () => {
    const { t } = useTranslation();
    const [reportData, setReportData] = useState<DeductionRecord[]>([]);
    const [reportLoading, setReportLoading] = useState<boolean>(false);
    const [reportNumberOfPages, setReportNumberOfPages] = useState<number>(1);
    const { customers } = useSelector((state: any) => state.customer || { customers: [] });
    
    const [filters, setFilters] = useState<ReportFilter>({
        startDate: '',
        endDate: '',
        customerFilter: '',
        searchQuery: ''
    });
    
    const [currentPage, setCurrentPage] = useState<number>(1);

    const fetchData = useCallback(async (pageNumber: number = 1) => {
        try {
            setReportLoading(true);
            const response = await fetchDeductionReport({ tab: 1, pageNumber });
            if (response && response.response) {
                setReportData(response.response);
                setReportNumberOfPages(response.numberOfPages || 1);
            }
        } catch (error) {
            console.error('Error fetching deduction report:', error);
        } finally {
            setReportLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(1);
    }, [fetchData]);

    // Helper function to get pricing type name from article number
    const getPricingTypeName = (articleNumber: string): string => {
        const pricingMap: { [key: string]: string } = {
            '92': 'Hourly',
            '94': 'Payment per day',
            '95': 'Payment per week',
            '96': 'Payment per month',
            '97': 'Payment per Agreed'
        };
        return pricingMap[articleNumber] || 'Hourly';
    };

    // Helper function to format hours from decimal to HH:MM
    const formatHours = (hours: number): string => {
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        return `${String(wholeHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    // Helper function to format date
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Map API response to display format
    const mappedReports: ReportData[] = useMemo(() => {
        if (!reportData || reportData.length === 0) return [];
        
        return reportData.map((item: any) => {
            // Get customer name from customers array
            let customerName = '';
            if (item.customerId && customers && customers.length > 0) {
                const customer = customers.find((c: any) => {
                    const custId = c.customerId || c.id || c.customer_id;
                    return custId === item.customerId;
                });
                customerName = customer ? (customer.name || customer.customerName || customer.customer_name || '') : '';
            }

            return {
                id: String(item.timeSheetWorkingHourId || item.id || 0),
                timeSheetWorkingHourId: item.timeSheetWorkingHourId,
                date: formatDate(item.workingDate),
                invoiceNo: item.invoiceNumber?.toString() || 'N/A',
                clientName: customerName,
                taskDetails: item.customWorkingDescription || '',
                pricingType: getPricingTypeName(item.articleNumber),
                price: `${item.amountPerUnit} SEK`,
                hours: formatHours(item.workingHour)
            };
        });
    }, [reportData, customers]);

    const [selectedReports, setSelectedReports] = useState<string[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const filteredReports = useMemo(() => {
        return mappedReports.filter(report => {
            const matchesSearch = filters.searchQuery
                ? report.clientName.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                  report.taskDetails.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                  report.date.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                  report.pricingType.toLowerCase().includes(filters.searchQuery.toLowerCase())
                : true;
            
            return matchesSearch;
        });
    }, [mappedReports, filters.searchQuery]);

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedReports([]);
        } else {
            setSelectedReports(filteredReports.map(report => report.id));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectReport = (id: string) => {
        if (selectedReports.includes(id)) {
            setSelectedReports(selectedReports.filter(reportId => reportId !== id));
        } else {
            setSelectedReports([...selectedReports, id]);
        }
    };

    const totalStats = useMemo(() => {
        return {
            count: filteredReports.length
        };
    }, [filteredReports]);

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, searchQuery: query }));
        setCurrentPage(1);
    };

    const handleBulkAction = (action: string) => {
        // Handle bulk actions here
    };

    const handleDownload = (report: ReportData) => {
        // Handle download
    };

    const handleDelete = (report: ReportData) => {
        // Handle delete
    };

    // Pagination - handled by API
    const totalPages = reportNumberOfPages || 1;

    const paginate = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        fetchData(pageNumber);
    };


    return (
        <div className="timesheet-reports">
            <TimesheetReportsHeader 
                onSearch={handleSearch}
                onBulkAction={handleBulkAction}
            />
            
            <div className="reports-table-wrapper">
                <div className="reports-data-table">
                    {/* Table Header */}
                    <div className="table-header-row">
                        <div className="table-cell date-cell">
                            <span className="header-title">{t('DATE') || 'Date'}</span>
                        </div>
                        <div className="table-cell invoice-cell">
                            <span className="header-title">{t('INVOICE_NO') || 'Invoice No.'}</span>
                        </div>
                        <div className="table-cell client-cell">
                            <span className="header-title">{t('CUSTOMER') || 'Customer'}</span>
                        </div>
                        <div className="table-cell task-cell">
                            <span className="header-title">{t('TASK_DETAILS_HEADER') || 'Task Details'}</span>
                        </div>
                        <div className="table-cell pricing-cell">
                            <span className="header-title">{t('PRICING_TYPE') || 'Pricing Type'}</span>
                        </div>
                        <div className="table-cell price-cell">
                            <span className="header-title">{t('PRICE_HEADER') || 'Price'}</span>
                        </div>
                        <div className="table-cell hours-cell">
                            <span className="header-title">{t('HOURS_HH_MM') || 'Hours (HH:MM)'}</span>
                        </div>
                    </div>

                    {/* Table Body */}
                    {filteredReports.length > 0 ? (
                        filteredReports.map((report) => (
                            <div key={report.id} className="table-body-row">
                                <div className="table-cell date-cell">
                                    <span className="report-date">{report.date}</span>
                                </div>
                                <div className="table-cell invoice-cell">
                                    <span className="report-invoice">{report.invoiceNo}</span>
                                </div>
                                <div className="table-cell client-cell">
                                    <span className="report-client">{report.clientName}</span>
                                </div>
                                <div className="table-cell task-cell">
                                    <span className="report-task">{report.taskDetails}</span>
                                </div>
                                <div className="table-cell pricing-cell">
                                    <span className="report-pricing">{report.pricingType}</span>
                                </div>
                                <div className="table-cell price-cell">
                                    <span className="report-price">{report.price}</span>
                                </div>
                                <div className="table-cell hours-cell">
                                    <span className="report-hours">{report.hours}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-data-row">
                            <p>{t('NO_REPORTS_FOUND')}</p>
                        </div>
                    )}
                </div>

               
            </div>
             {/* Pagination */}
                <div className="table-pagination">
                    <button 
                        className="pagination-btn prev-btn"
                        disabled={currentPage === 1 || reportLoading}
                        onClick={() => paginate(currentPage - 1)}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 12L6 8L10 4" stroke="#68727D" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('PREVIOUS')}</span>
                    </button>
                    
                    {(() => {
                        const pages = [];
                        const maxPagesToShow = 6;
                        
                        if (totalPages <= maxPagesToShow) {
                            // Show all pages if total is less than max
                            for (let i = 1; i <= totalPages; i++) {
                                pages.push(
                                    <button
                                        key={i}
                                        className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                        onClick={() => paginate(i)}
                                        disabled={reportLoading}
                                    >
                                        {i}
                                    </button>
                                );
                            }
                        } else {
                            // Show first page
                            pages.push(
                                <button
                                    key={1}
                                    className={`pagination-number ${currentPage === 1 ? 'active' : ''}`}
                                    onClick={() => paginate(1)}
                                    disabled={reportLoading}
                                >
                                    1
                                </button>
                            );
                            
                            // Show dots if current page is far from start
                            if (currentPage > 3) {
                                pages.push(<span key="dots-start" className="pagination-number">...</span>);
                            }
                            
                            // Show pages around current page
                            const startPage = Math.max(2, currentPage - 1);
                            const endPage = Math.min(totalPages - 1, currentPage + 1);
                            
                            for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                    <button
                                        key={i}
                                        className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                        onClick={() => paginate(i)}
                                        disabled={reportLoading}
                                    >
                                        {i}
                                    </button>
                                );
                            }
                            
                            // Show dots if current page is far from end
                            if (currentPage < totalPages - 2) {
                                pages.push(<span key="dots-end" className="pagination-number">...</span>);
                            }
                            
                            // Show last page
                            pages.push(
                                <button
                                    key={totalPages}
                                    className={`pagination-number ${currentPage === totalPages ? 'active' : ''}`}
                                    onClick={() => paginate(totalPages)}
                                    disabled={reportLoading}
                                >
                                    {totalPages}
                                </button>
                            );
                        }
                        
                        return pages;
                    })()}
                    
                    <button 
                        className="pagination-btn next-btn"
                        disabled={currentPage === totalPages || reportLoading}
                        onClick={() => paginate(currentPage + 1)}
                    >
                        <span>{t('NEXT')}</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 4L10 8L6 12" stroke="#68727D" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
        </div>
    );
};

export default TimesheetReports;
